import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { getDashboardSummary, getBalanceSummary } from '@/api/client';

export default function OverviewPage() {
  const [data, setData] = React.useState<any>(null);
  const [balance, setBalance] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    Promise.all([
      getDashboardSummary(),
      getBalanceSummary().catch(() => null),
    ])
      .then(([d, b]) => {
        setData(d);
        setBalance(b);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">首页总览</h1>
        <p className="mt-1 text-sm text-muted-foreground">米粒AI无人公司 1.0 核心状态</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data?.founderPulse ? (
          <>
            <MetricCard
              title="今日主线"
              value={data.founderPulse.todayMainGoal || '—'}
            />
            <MetricCard
              title="综合能量值"
              value={data.founderPulse.timeManagement?.utilizationScore != null
                ? `${data.founderPulse.timeManagement.utilizationScore.toFixed(1)} 分`
                : '—'}
            />
            <MetricCard
              title="今日完成率"
              value={data.founderPulse.todayCompletion != null
                ? `${data.founderPulse.todayCompletion}%`
                : '—'}
            />
          </>
        ) : (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))
        )}
      </div>

      {/* Token 余额 */}
      {balance && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">模型平台余额</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              {balance.platforms?.map((p: any) => {
                const isMiniMax = p.code === 'minimax' || p.name?.includes('MiniMax');
                return (
                  <div key={p.code} className="rounded-lg border p-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color || '#888' }} />
                      <span className="text-xs font-medium text-muted-foreground">{p.name}</span>
                    </div>
                    <div className="mt-1.5 text-lg font-bold">{p.displayBalance || '—'}</div>
                    {p.balanceUnit && (
                      <div className="text-xs text-muted-foreground">{p.balanceKind || ''} {p.balanceUnit}</div>
                    )}
                    {isMiniMax && (
                      <div className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-500">
                        <span>⏳ T+1 更新</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {balance.totalCnyBalance > 0 && (
              <div className="mt-3 text-right text-sm text-muted-foreground">
                人民币账户合计: <span className="font-semibold text-foreground">{balance.displayTotalCnyBalance}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MetricCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}